/**
 * Mass Messages → Agenda: een kalender van de geplande Mass Messages.
 *
 * 03-10-2026 (Jordy). Alleen voor owner, admin en manager (de server weigert
 * de rest ook: `mass-message/agenda`). Standaard de huidige week (ma–zo), met
 * Prev / Today / Next en een knop naar de maand. Alles in Amsterdamse tijd,
 * los van de tijdzone van de browser (lib/agenda.ts en lib/amsterdam.ts).
 *
 * Elk item: tijd, model (foto en naam), platform, methode, het begin van de
 * tekst, een miniatuur van de content en de prijs (FanCentro in credits, F2F
 * in euro). Een klik opent de details met Cancel (met bevestiging) en Edit
 * (datum/tijd, tekst en — 04-10-2026 — gratis/betaald en de prijs, zolang het
 * item nog niet gestart is; dezelfde prijsregels als bij plannen).
 * Overgeslagen en mislukte items staan er grijs bij, met de reden.
 *
 * Er wordt hier niets verstuurd.
 *
 * 07-10-2026 (Jordy): dagen en tijden staan in de tijdzone van de gebruiker
 * (lib/tijdzone.ts; zonder voorkeur Amsterdam), met de zone erbij. Bewerken
 * leest de tijd in diezelfde zone; een ongewijzigde tijd gaat niet mee.
 */
import { useMemo, useState } from 'react'
import { Field, Modal } from '../components/Modal'
import { TijdKiezer } from '../components/TijdKiezer'
import { PogingenLijst, RetryMassaDialoog } from '../components/RetryMassaDialoog'
import { Avatar, ErrorNote, Loading, Plat, Seg, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { thumbUrl } from '../lib/api'
import { prijsVoorPlatform } from '../lib/format'
import { magBetaaldeMassDm, useMij } from '../lib/rechten'
import { eenheidVoor, isF2f, minimumVoor, prijsAlsGetal, prijsFouten, prijsPerFanTekst } from '../lib/massPrijs'
import { BETAALD_ZONDER_CONTENT } from '../components/MassDmFormulier'
import { metZone, metZoneEnAmsterdam, zoneDelen, zoneLabel, zoneNaarUtc } from '../lib/amsterdam'
import {
  dagLabel, maandagVan, maandLabel, maandWeken, perDag, plusDagen,
  plusMaanden, vandaagAmsterdam, vensterUtc, weekDagen, weekLabel,
} from '../lib/agenda'
import { useBijZoneWissel, useMijnZone } from '../lib/tijdzone'
import type { Persona } from './Models'

/** Zelfde grenzen als de server (`_parse_schedule_at_or_400`). */
const MIN_VOORUIT_MS = 2 * 60_000
const MAX_VOORUIT_MS = 90 * 86_400_000
const AGENDA = '/api/admin/conversations/mass-message/agenda'

/** Eén item zoals `mass-message/agenda` hem teruggeeft. */
export interface AgendaItem {
  id: number
  platform: string
  model_slug: string | null
  model_name: string | null
  content: string | null
  has_media: boolean
  media_count: number
  content_ids: number[]
  is_paid: boolean
  price: number
  only_test: boolean
  fc_delivery_mode: string
  exclude_recent_minutes: number
  exclude_timewasters: boolean
  exclude_maybe_timewasters?: boolean
  audience_include: string | null
  exclude_spenders: boolean
  exclude_new_followers: boolean
  exclude_private_chats: boolean
  online_only: boolean
  translation_enabled: boolean
  translate_to: string | null
  scheduled_at: string | null
  status: string
  error_message: string | null
  created_by: string | null
  created_at: string | null
  editable: boolean
  /** 16-10-2026 (Jordy): een al VERZONDEN Mass Message (uit de verzendhistorie). */
  verzonden?: { recipients: number; delivered: number; failed: number }
}

/** Eén verzonden Mass Message zoals `agenda?include_sent=1` hem in `sent` teruggeeft. */
interface Verzonden {
  log_id: number; sent_at: string | null; platform: string; model_slug: string; model_name: string
  content: string; kind: string; has_media: boolean; is_paid: boolean; price: number
  recipients: number; delivered: number; failed: number; status: string; only_test: boolean; created_by: string
}

/** Als agenda-item: gedimd, niet te bewerken, status "sent". Negatief id: nooit gelijk aan een gepland id. */
function alsItem(v: Verzonden): AgendaItem {
  return {
    id: -v.log_id, platform: v.platform, model_slug: v.model_slug, model_name: v.model_name, content: v.content,
    has_media: v.has_media, media_count: 0, content_ids: [], is_paid: v.is_paid, price: v.price,
    only_test: v.only_test, fc_delivery_mode: v.kind.includes('native') ? 'native' : 'direct',
    exclude_recent_minutes: 0, exclude_timewasters: false, audience_include: null, exclude_spenders: false,
    exclude_new_followers: false, exclude_private_chats: false, online_only: false, translation_enabled: false,
    translate_to: null, scheduled_at: v.sent_at, status: 'sent', error_message: null, created_by: v.created_by,
    created_at: v.sent_at, editable: false,
    verzonden: { recipients: v.recipients, delivered: v.delivered, failed: v.failed },
  }
}

const WEERGAVEN = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

function methodeVan(it: AgendaItem): string {
  return it.fc_delivery_mode === 'native' ? 'Platform Mass DM' : 'Dashboard Mass Message'
}

function prijsVan(it: AgendaItem): string | null {
  if (it.is_paid) return prijsVoorPlatform(it.price, it.platform)
  return it.has_media ? 'Free' : null
}

/** Grijs: overgeslagen of mislukt; die gaan niet (meer) uit. */
function isGrijs(it: AgendaItem): boolean {
  return it.status === 'skipped' || it.status === 'failed'
}

/** Al verzonden (16-10-2026): gedimd, met vinkje en de aantallen. */
function isVerzonden(it: AgendaItem): boolean {
  return it.status === 'sent'
}

function StatusTag({ it }: { it: AgendaItem }) {
  // 11-10-2026 (Jordy): concepten staan ook in de kalender, als concept gemarkeerd.
  if (it.status === 'draft') return <Tag kind="mut" title="Not approved yet: it is never sent like this.">draft · not approved</Tag>
  if (it.status === 'skipped') return <Tag kind="bad">skipped · not sent</Tag>
  if (it.status === 'failed') return <Tag kind="bad">failed</Tag>
  if (it.status === 'sending') return <Tag kind="warn">sending</Tag>
  if (it.status === 'sent' && it.verzonden) {
    return (
      <Tag kind="ok" title={`${it.verzonden.recipients} recipients`}>
        ✓ sent · {it.verzonden.delivered} delivered{it.verzonden.failed ? ` · ${it.verzonden.failed} failed` : ''}
      </Tag>
    )
  }
  return null
}

export function MassaAgenda() {
  const personas = useApi<Persona[]>('/api/admin/models/personas')
  const [weergave, setWeergave] = useState<'week' | 'month'>('week')
  const zone = useMijnZone()
  const [anker, setAnker] = useState(() => vandaagAmsterdam(undefined, zone))
  // /me komt na het eerste tekenen binnen: dan "vandaag" in de eigen zone.
  useBijZoneWissel(zone, (_van, naar) => setAnker(vandaagAmsterdam(undefined, naar)))
  const [model, setModel] = useState('all')
  const [open, setOpen] = useState<AgendaItem | null>(null)
  const vandaag = vandaagAmsterdam(undefined, zone)

  const weken = weergave === 'week' ? [weekDagen(maandagVan(anker))] : maandWeken(anker)
  const venster = vensterUtc(weken[0][0], weken[weken.length - 1][6], zone)
  const q = useApi<{ items: AgendaItem[]; sent?: Verzonden[] }>(
    `${AGENDA}?from=${encodeURIComponent(venster.from)}&to=${encodeURIComponent(venster.to)}&include_drafts=1&include_sent=1`)

  // Modelfoto per slug, uit de bestaande personalijst.
  const fotos = useMemo(() => {
    const m: Record<string, string | null> = {}
    for (const p of personas.data ?? []) {
      m[p.slug] = p.avatar_url
      for (const s of p.persona_slugs ?? []) if (!(s in m)) m[s] = p.avatar_url
    }
    return m
  }, [personas.data])

  // 16-10-2026 (Jordy): naast wat gepland staat ook wat al verzonden is.
  const alle = useMemo(() => [...(q.data?.items ?? []), ...(q.data?.sent ?? []).map(alsItem)], [q.data])
  const modellen = useMemo(() => {
    const m = new Map<string, string>()
    for (const it of alle) {
      const slug = it.model_slug || ''
      if (slug && !m.has(slug)) {
        m.set(slug, `${it.model_name || slug} — ${it.platform === 'f2f' ? 'F2F' : 'FanCentro'}`)
      }
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [alle])
  const zichtbaar = model === 'all' ? alle : alle.filter((it) => it.model_slug === model)
  const dagen = useMemo(() => perDag(zichtbaar, zone), [zichtbaar, zone])
  const gepland = zichtbaar.filter((it) => !isGrijs(it) && !isVerzonden(it)).length
  const verzondenAantal = zichtbaar.filter(isVerzonden).length

  const stap = (n: number) => setAnker((a) => (weergave === 'week' ? plusDagen(a, 7 * n) : plusMaanden(a, n)))
  const eenheid = weergave === 'week' ? 'week' : 'month'

  return (
    <>
      <div className="filters agenda-toolbar">
        <Seg options={WEERGAVEN} value={weergave} onChange={(v) => setWeergave(v as 'week' | 'month')} />
        <div className="row" style={{ gap: 6 }}>
          <button className="btn ghost sm" type="button" aria-label={`Previous ${eenheid}`} onClick={() => stap(-1)}>‹ Prev</button>
          <button className="btn ghost sm" type="button" onClick={() => setAnker(vandaagAmsterdam(undefined, zone))}>Today</button>
          <button className="btn ghost sm" type="button" aria-label={`Next ${eenheid}`} onClick={() => stap(1)}>Next ›</button>
        </div>
        <b data-testid="agenda-period" style={{ fontSize: 14 }}>
          {weergave === 'week' ? weekLabel(weken[0][0]) : maandLabel(anker)}
        </b>
        <span className="spacer" style={{ flex: 1 }} />
        <select className="inp" aria-label="Model filter" style={{ width: 'auto' }} value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="all">All models</option>
          {model !== 'all' && !modellen.some(([s]) => s === model)
            ? <option value={model}>{model}</option> : null}
          {modellen.map(([s, label]) => <option key={s} value={s}>{label}</option>)}
        </select>
        <span className="mut2" style={{ fontSize: 12 }} data-testid="agenda-time-zone">
          {gepland} scheduled · {verzondenAantal} sent · {zoneLabel(zone)} time
        </span>
      </div>

      {q.loading && !q.data ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : null}

      {weergave === 'week' ? (
        <div className="agenda-week">
          {weken[0].map((dag) => (
            <div key={dag} className={dag === vandaag ? 'agenda-day agenda-today' : 'agenda-day'} data-day={dag}>
              <div className="agenda-day-head">{dagLabel(dag)}</div>
              {(dagen[dag] ?? []).map((it) => (
                <ItemKaart key={it.id} it={it} foto={fotos[it.model_slug || '']} zone={zone} onOpen={() => setOpen(it)} />
              ))}
              {(dagen[dag] ?? []).length === 0 ? <div className="dim agenda-empty">—</div> : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="agenda-month">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div key={d} className="agenda-month-head">{d}</div>
          ))}
          {weken.flat().map((dag) => {
            const rij = dagen[dag] ?? []
            const buiten = dag.slice(0, 7) !== anker.slice(0, 7)
            const vandaagCel = dag === vandaag
            const klassen = ['agenda-cell', buiten ? 'agenda-outside' : null, vandaagCel ? 'agenda-today' : null]
              .filter(Boolean).join(' ')
            return (
              <div key={dag} className={klassen} data-day={dag}>
                <div className="agenda-cell-num">{Number(dag.slice(8))}</div>
                {rij.slice(0, 4).map((it) => (
                  <button key={it.id} type="button" title={it.error_message || undefined}
                          className={isGrijs(it) ? 'agenda-chip agenda-grey' : 'agenda-chip'}
                          style={isVerzonden(it) ? { opacity: 0.6 } : undefined}
                          onClick={() => setOpen(it)}>
                    <span className="mono" title={metZone(it.scheduled_at, zone)}>{zoneDelen(it.scheduled_at, zone)?.tijd}</span>
                    <Avatar name={it.model_name} src={fotos[it.model_slug || '']} size={16} />
                    <span className="afkap">{isVerzonden(it) ? '✓ ' : ''}{it.model_name || it.model_slug}</span>
                  </button>
                ))}
                {rij.length > 4 ? (
                  <button type="button" className="btn ghost sm agenda-more"
                          onClick={() => { setAnker(dag); setWeergave('week') }}>
                    +{rij.length - 4} more
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
      )}

      {open ? (
        <Detail it={open} foto={fotos[open.model_slug || '']} zone={zone}
                onClose={() => setOpen(null)}
                onChanged={() => { setOpen(null); q.refetch() }} />
      ) : null}
    </>
  )
}

/** Eén item in de weekweergave. */
function ItemKaart({ it, foto, zone, onOpen }: { it: AgendaItem; foto?: string | null; zone: string; onOpen: () => void }) {
  const tijd = zoneDelen(it.scheduled_at, zone)?.tijd ?? '—'
  const prijs = prijsVan(it)
  const eerste = it.content_ids?.[0]
  return (
    <button type="button" className={isGrijs(it) ? 'agenda-item agenda-grey' : 'agenda-item'}
            title={it.error_message || undefined} onClick={onOpen} data-id={it.id}
            data-draft={it.status === 'draft' ? '1' : undefined}
            data-sent={isVerzonden(it) ? '1' : undefined}
            style={it.status === 'draft' ? { borderStyle: 'dashed', opacity: 0.8 }
              : isVerzonden(it) ? { opacity: 0.6 } : undefined}>
      <div className="row" style={{ gap: 6, justifyContent: 'space-between' }}>
        <b className="mono agenda-time" title={metZone(it.scheduled_at, zone)}>{tijd}</b>
        <Plat value={it.platform} />
      </div>
      <div className="row" style={{ gap: 6, marginTop: 4, minWidth: 0 }}>
        <Avatar name={it.model_name} src={foto} size={20} />
        <span className="afkap" style={{ fontWeight: 600 }}>{it.model_name || it.model_slug || '—'}</span>
      </div>
      <div className="mut2 agenda-method">{methodeVan(it)}</div>
      {it.content ? <div className="agenda-text">{it.content}</div> : null}
      {it.has_media || prijs ? (
        <div className="row" style={{ gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          {eerste ? <img className="agenda-thumb" src={thumbUrl(eerste)} alt="" loading="lazy" /> : null}
          {it.has_media && !eerste ? <Tag kind="mut">media</Tag> : null}
          {prijs ? <Tag kind={it.is_paid ? 'acc' : 'mut'}>{prijs}</Tag> : null}
        </div>
      ) : null}
      {it.only_test ? <Tag kind="warn">test</Tag> : null}
      <StatusTag it={it} />
      {isGrijs(it) && it.error_message ? <div className="agenda-reason">{it.error_message}</div> : null}
    </button>
  )
}

function doelgroep(it: AgendaItem): string {
  if (it.only_test) return 'Test accounts only'
  const filters = [
    it.exclude_recent_minutes ? `exclude replies in last ${Math.round(it.exclude_recent_minutes / 60)}h` : '',
    it.exclude_timewasters ? 'exclude timewasters' : '',
    it.exclude_maybe_timewasters ? 'exclude maybe timewasters' : '',
    it.exclude_spenders ? 'exclude spenders' : '',
    it.exclude_new_followers ? 'exclude new followers' : '',
    it.exclude_private_chats ? 'exclude private chats' : '',
    it.online_only ? 'online only' : '',
  ].filter(Boolean)
  return [it.audience_include || 'all', ...filters].join(' · ')
}

const RETRY_BASIS = '/api/admin/conversations/mass-message/agenda'

/** Details van één item, met Cancel en Edit. */
function Detail({ it, foto, zone, onClose, onChanged }: {
  it: AgendaItem
  foto?: string | null
  zone: string
  onClose: () => void
  onChanged: () => void
}) {
  const { run, busy } = useActions()
  const delen = zoneDelen(it.scheduled_at, zone)
  const [bewerken, setBewerken] = useState(false)
  // 06-10-2026 (Jordy): een mislukte Mass Message opnieuw proberen.
  const [retry, setRetry] = useState(false)
  const [datum, setDatum] = useState(delen?.datum ?? '')
  const [tijd, setTijd] = useState(delen?.tijd ?? '')
  const [tekst, setTekst] = useState(it.content ?? '')
  // 04-10-2026 (Jordy): ook gratis/betaald en de prijs, met dezelfde regels als
  // bij plannen (minimumprijs, recht op betaalde Mass DM, alleen met content).
  const magBetaald = magBetaaldeMassDm(useMij().data)
  const [betaald, setBetaald] = useState(Boolean(it.is_paid))
  const [prijsInvoer, setPrijsInvoer] = useState(it.is_paid ? String(it.price) : '')
  const [geprobeerd, setGeprobeerd] = useState(false)
  const prijs = prijsVan(it)
  const aantalMedia = it.has_media ? (it.media_count || it.content_ids.length || 1) : 0

  // 07-10-2026: dezelfde datum en tijd als bij openen = precies het oude
  // moment (ook in het dubbele uur van de wintertijd); anders gelezen in `zone`.
  const ongewijzigd = Boolean(delen) && datum === delen?.datum && tijd === delen?.tijd
  const moment = ongewijzigd ? it.scheduled_at : zoneNaarUtc(datum, tijd, zone)
  const meldingen: string[] = []
  if (!moment) meldingen.push('Pick a valid date and time.')
  else if (Date.parse(moment) !== Date.parse(it.scheduled_at || '')) {
    if (Date.parse(moment) < Date.now() + MIN_VOORUIT_MS) meldingen.push('Pick a time at least 2 minutes from now.')
    else if (Date.parse(moment) > Date.now() + MAX_VOORUIT_MS) meldingen.push('Cannot schedule more than 90 days ahead.')
  }
  if (!tekst.trim() && !it.has_media) meldingen.push('Write a message or add a photo or video.')
  if (!tekst.trim() && it.platform === 'f2f' && it.fc_delivery_mode === 'native') {
    meldingen.push('F2F needs a message with a Platform Mass DM.')
  }
  const prijsGetal = prijsAlsGetal(prijsInvoer)
  const prijsAnders = betaald !== Boolean(it.is_paid)
    || (betaald && prijsGetal !== Number(it.price))
  // 05-10-2026 (Jordy): de prijskeuze is altijd zichtbaar; zonder content kan
  // een gepland bericht niet betaald worden (de server weigert het ook, 400).
  if (betaald && !it.has_media) meldingen.push(BETAALD_ZONDER_CONTENT)
  else if (it.has_media && prijsAnders) {
    if (betaald && !magBetaald) meldingen.push('Paid Mass DMs aren’t enabled for your account. Make it free.')
    else meldingen.push(...prijsFouten(it.platform, betaald, prijsInvoer))
  }
  const body: { scheduled_at?: string; content?: string; is_paid?: boolean; price?: number } = {}
  if (moment && Date.parse(moment) !== Date.parse(it.scheduled_at || '')) body.scheduled_at = moment
  if (tekst.trim() !== (it.content ?? '').trim()) body.content = tekst.trim()
  // 05-10-2026: ook zonder content meesturen; de server weigert betaald zonder content.
  if (prijsAnders) {
    body.is_paid = betaald
    if (betaald) body.price = prijsGetal
  }
  const gewijzigd = Object.keys(body).length > 0

  const opslaan = () => {
    setGeprobeerd(true)
    if (meldingen.length || !gewijzigd) return
    void run({
      label: 'Edit scheduled Mass Message',
      request: mut.patch(`/api/admin/conversations/mass-message/agenda/${it.id}`, body),
      onDone: onChanged,
      successText: 'Scheduled Mass Message updated.',
    })
  }
  const annuleren = () => void run({
    label: 'Cancel scheduled Mass Message',
    confirm: `Cancel the Mass Message for ${it.model_name || it.model_slug} on ${metZone(it.scheduled_at, zone)}? Nothing will be sent.`,
    request: mut.post(`/api/admin/conversations/mass-message/agenda/${it.id}/cancel`),
    onDone: onChanged,
    successText: 'Scheduled Mass Message cancelled.',
  })

  if (retry) {
    return (
      <RetryMassaDialoog basis={RETRY_BASIS} id={it.id} echteFans={!it.only_test} zone={zone}
                         onClose={() => setRetry(false)}
                         onKlaar={() => { onChanged(); onClose() }} />
    )
  }
  return (
    <Modal
      title={bewerken ? 'Edit scheduled Mass Message' : 'Scheduled Mass Message'}
      desc={<span className="row" style={{ gap: 6 }}>
        <Avatar name={it.model_name} src={foto} size={18} /> {it.model_name || it.model_slug}
        {' '}<Plat value={it.platform} /> · {metZone(it.scheduled_at, zone)}
      </span>}
      width={620}
      onClose={onClose}
      footer={bewerken ? (
        <>
          <button className="btn ghost sm" type="button" onClick={() => setBewerken(false)}>Back</button>
          <button className="btn sm" type="button" disabled={busy === 'Edit scheduled Mass Message'}
                  onClick={opslaan}>Save changes</button>
        </>
      ) : (
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Close</button>
          {it.status === 'scheduled' ? (
            <button className="btn ghost sm" type="button" style={{ color: 'var(--bad)' }}
                    disabled={busy === 'Cancel scheduled Mass Message'} onClick={annuleren}>
              Cancel Mass Message
            </button>
          ) : null}
          {it.editable ? (
            <button className="btn sm" type="button" onClick={() => setBewerken(true)}>Edit</button>
          ) : null}
          {it.status === 'failed' ? (
            <button className="btn sm" type="button" data-testid="agenda-retry"
                    onClick={() => setRetry(true)}>Retry…</button>
          ) : null}
        </>
      )}
    >
      {bewerken ? (
        <div className="agenda-edit">
          {/* 04-10-2026 (Jordy): dezelfde tijdkiezer als Create, zonder typveld. */}
          <TijdKiezer datum={datum} tijd={tijd} zone={zone} onChange={(m) => { setDatum(m.datum); setTijd(m.tijd) }} />
          <Field label="Message">
            <textarea className="inp" aria-label="Message" rows={5} value={tekst}
                      onChange={(e) => setTekst(e.target.value)} />
          </Field>
          {it.translation_enabled ? (
            <div className="mut2" style={{ fontSize: 12, marginTop: 6 }}>
              The translator is on: this text is translated when the Mass Message is sent.
            </div>
          ) : null}
          {/* 05-10-2026 (Jordy): altijd zichtbaar, ook zonder content. */}
          {(
            <Field label={`Price per fan (${isF2f(it.platform) ? 'F2F, in €' : 'FanCentro, in credits'})`}
                   hint={`Minimum for a paid Mass Message: ${isF2f(it.platform) ? '€' : ''}${minimumVoor(it.platform)}${isF2f(it.platform) ? '' : ' credits'}. One price for the whole bundle.`}>
              <div className="massdm-prijs" data-testid="agenda-prijs" style={{ marginTop: 0 }}>
                <div className="seg" role="group" aria-label="Free or paid">
                  <button type="button" className={betaald ? undefined : 'on'} aria-pressed={!betaald}
                          onClick={() => setBetaald(false)}>
                    Send free
                  </button>
                  <button type="button" className={betaald ? 'on' : undefined} aria-pressed={betaald}
                          disabled={!magBetaald && !betaald}
                          title={magBetaald ? undefined : 'Paid Mass DMs aren’t enabled for your account.'}
                          onClick={() => setBetaald(true)}>
                    Paid unlock
                  </button>
                </div>
                {betaald ? (
                  <span className="row massdm-prijsveld" style={{ gap: 6 }}>
                    {isF2f(it.platform) ? <span className="mut2">€</span> : null}
                    <input className="inp" type="number" aria-label={`Price per fan (${eenheidVoor(it.platform)})`}
                           min={minimumVoor(it.platform)} step={isF2f(it.platform) ? '0.01' : '1'}
                           disabled={!magBetaald} value={prijsInvoer}
                           onChange={(e) => setPrijsInvoer(e.target.value)} />
                    {isF2f(it.platform) ? null : <span className="mut2">credits</span>}
                  </span>
                ) : null}
                {/* 05-10-2026 (Jordy): direct bij het prijsveld, niet pas bij opslaan. */}
                {betaald && !it.has_media ? (
                  <span className="tag bad" data-testid="betaald-zonder-content">{BETAALD_ZONDER_CONTENT}</span>
                ) : null}
                <span className={betaald ? 'tag acc' : 'tag mut'}>
                  {prijsPerFanTekst(it.platform, betaald, prijsInvoer, aantalMedia)}
                </span>
              </div>
            </Field>
          )}
          <div className="mut2" style={{ fontSize: 12, marginTop: 6 }}>
            {it.has_media
              ? 'The time, the text and the price can be changed here. Model, audience and content stay the same.'
              : 'The time and the text can be changed here. Model and audience stay the same; a text-only Mass Message has no price.'}
          </div>
          {geprobeerd && (meldingen.length || !gewijzigd) ? (
            <ul className="massa-slot-errors">
              {meldingen.map((m) => <li key={m}>{m}</li>)}
              {!meldingen.length && !gewijzigd ? <li>Nothing changed.</li> : null}
            </ul>
          ) : null}
        </div>
      ) : (
        <>
          <div className="kv" style={{ marginBottom: 12 }}>
            <span>When</span><span className="mono">{metZoneEnAmsterdam(it.scheduled_at, zone)}</span>
            <span>Model</span><span>{it.model_name || it.model_slug || '—'}</span>
            <span>Platform</span><span>{it.platform === 'f2f' ? 'F2F' : 'FanCentro'}</span>
            <span>Method</span><span>{methodeVan(it)}</span>
            <span>Audience</span><span>{doelgroep(it)}</span>
            {it.translation_enabled || it.translate_to ? (
              <><span>Translation</span><span>→ {it.translate_to || 'nl'}</span></>
            ) : null}
            <span>Content</span>
            <span>{it.has_media ? `${it.media_count || it.content_ids.length} item(s)` : 'Text only'}</span>
            {prijs ? (
              <><span>Price</span><span className="mono" data-testid="agenda-detail-prijs">
                {prijsPerFanTekst(it.platform, it.is_paid, it.price, aantalMedia)}
              </span></>
            ) : null}
            <span>Status</span><span>{it.status} <StatusTag it={it} /></span>
            {it.error_message ? <><span>Reason</span><span>{it.error_message}</span></> : null}
            {it.status === 'failed' ? (
              <><span>Attempts</span><span><PogingenLijst basis={RETRY_BASIS} id={it.id} zone={zone} /></span></>
            ) : null}
            <span>Created by</span><span>{it.created_by || '—'}</span>
            <span>Created</span><span className="mono">{metZone(it.created_at, zone)}</span>
          </div>
          <b style={{ fontSize: 12.5 }}>Message</b>
          <div className="note" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{it.content || '(no text)'}</div>
          {it.content_ids?.length ? (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {it.content_ids.map((id) => (
                <img key={id} className="agenda-thumb-big" src={thumbUrl(id)} alt="" loading="lazy" />
              ))}
            </div>
          ) : null}
          {it.status === 'draft' ? (
            <div className="note" style={{ marginTop: 10 }} data-testid="agenda-draft-note">
              This is a draft and is never sent until someone approves it. Review it under Drafts or Auto.
            </div>
          ) : null}
          {it.status === 'scheduled' && !it.editable ? (
            <div className="note" style={{ marginTop: 10 }}>
              This Mass Message is about to start and can no longer be edited.
            </div>
          ) : null}
        </>
      )}
    </Modal>
  )
}
